import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Language, translations } from '@/lib/translations';

type TranslationType = typeof translations.de | typeof translations.en | typeof translations.pt;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationType;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('h2h-language');
    return (saved === 'en' || saved === 'de' || saved === 'pt') ? saved : 'de';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('h2h-language', lang);
    // Update HTML lang attribute for accessibility and SEO
    document.documentElement.lang = lang;
  };
  
  const t = translations[language];
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback (e.g. during HMR when the context module instance is swapped)
    // so the app never crashes into a blank screen.
    if (import.meta.env.DEV) {
      console.warn('useLanguage used outside LanguageProvider — falling back to default language.');
    }
    return { language: 'de', setLanguage: () => {}, t: translations.de };
  }
  return context;
};
